import { ItiLink as Link, Loader } from 'components';
import { useAuth } from 'contexts';
import { UnitTransfer, useUnitApi } from 'hooks/use-unit-api';
import { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getBaseUnit, getBookingByUnitId } from 'services';
import { ActiveUnit, Booking, Role } from 'types';

const GeneralInformation: FC<Partial<ActiveUnit>> = ({
  unitNumber,
  sizeType,
  unitStatus,
  statusReason,
  customer,
  location,
  unitGrade,
  ...unit
}) => {
  const { userProfile } = useAuth();
  const params = useParams();
  const { getUnitTransfers } = useUnitApi();

  const [baseUnit, setBaseUnit] = useState<ActiveUnit | null>();
  const [booking, setBooking] = useState<Booking | null>();
  const [unitTransfers, setUnitTransfers] = useState<UnitTransfer[]>();

  const transferredFrom = useMemo(() => {
    return unitTransfers?.find(({ newUnit }) => newUnit.id === unit.id)?.oldUnit ?? null;
  }, [unit.id, unitTransfers]);

  const transferredTo = useMemo(() => {
    return unitTransfers?.find(({ oldUnit }) => oldUnit.id === unit.id)?.newUnit ?? null;
  }, [unit.id, unitTransfers]);

  useEffect(() => {
    if (unit.id) {
      getBaseUnit(unit.id).then(setBaseUnit);
      getBookingByUnitId(unit.id).then(setBooking);
      if (
        [
          Role.ADMIN,
          Role.BILLING_ADMIN,
          Role.SENIOR_BOOKING,
          Role.BOOKING,
          Role.ADMIN_CUSTOMER,
          Role.CUSTOMER,
          Role.EXTERNAL
        ].includes(userProfile.role)
      ) {
        getUnitTransfers(unit.id).then(setUnitTransfers);
      }
    }
  }, [getUnitTransfers, setBaseUnit, setBooking, unit.id, userProfile.role]);

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>UNIT DETAILS</h4>
      </div>
      <div className="card-body">
        <div className="p-3 grid-auto">
          <div>
            <h6 className="text-dark font-weight-regular">UNIT NUMBER</h6>
            <p className="text-black" data-cy="unit-number">
              {unitNumber ?? params.unitNumber ?? 'N/A'}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">SIZE TYPE</h6>
            <p className="text-black" data-cy="unit-size-type">
              {sizeType ?? 'N/A'}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">STATUS</h6>
            <p className="text-black" data-cy="unit-status">
              {unitStatus ?? 'N/A'}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">STATUS NOTES</h6>
            <p className="text-black" data-cy="unit-status-reason">
              {statusReason ?? 'N/A'}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">ACCOUNT</h6>
            <p className="text-black" data-cy="unit-customer">
              {customer?.name ?? 'N/A'}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">LOCATION</h6>
            <p className="text-black" data-cy="unit-location">
              {location?.name ?? 'N/A'}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">GRADE</h6>
            <p className="text-black" data-cy="unit-grade">
              {unitGrade ?? 'N/A'}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">NOTES</h6>
            <p className="text-black" data-cy="unit-notes">
              {unit.notes ?? 'N/A'}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">DAYS IN YARD</h6>
            <p className="text-black">{unit.daysInYard ?? 'N/A'}</p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">MOUNTED ON</h6>
            <Loader loading={baseUnit === undefined}>
              {!baseUnit ? (
                <p className="text-black">N/A</p>
              ) : (
                <Link to={`/units/${baseUnit.id}`} data-cy="unit-base-unit-link">
                  {baseUnit.unitNumber}
                </Link>
              )}
            </Loader>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">ON HOLD</h6>
            <p className="text-black" data-cy="unit-on-hold">
              {unit.onHold ? 'YES' : 'NO'}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">HOLD NOTES</h6>
            <p className="text-black" data-cy="unit-hold-notes">
              {unit.holdNotes ?? 'N/A'}
            </p>
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">REDELIVERY NUMBER</h6>
            {!unit.redeliveryId || !unit.redeliveryNumber ? (
              <p className="text-black" data-cy="unit-redelivery-number">
                {unit.redeliveryNumber ?? 'N/A'}
              </p>
            ) : (
              <Link to={`/redeliveries/${unit.redeliveryId}`} data-cy="unit-redelivery-link">
                {unit.redeliveryNumber}
              </Link>
            )}
          </div>
          <div>
            <h6 className="text-dark font-weight-regular">BOOKING</h6>
            <Loader loading={booking === undefined}>
              {!booking ? (
                <p className="text-black">N/A</p>
              ) : (
                <Link to={`/bookings/${booking.id}`} data-cy="unit-booking-link">
                  {booking.bookingNumber}
                </Link>
              )}
            </Loader>
          </div>
          {!!unitTransfers?.length && (
            <>
              <div>
                <h6 className="text-dark font-weight-regular">RENUMBERED FROM</h6>
                {!transferredFrom ? (
                  <p className="text-black">N/A</p>
                ) : [Role.ADMIN_CUSTOMER, Role.CUSTOMER, Role.EXTERNAL].includes(userProfile.role) &&
                  !userProfile.customers.some(({ id }) => id === transferredFrom.customer?.id) ? (
                  <p className="text-black" data-cy="unit-renumbered-from">
                    {transferredFrom.unitNumber}({transferredFrom.customer?.name ?? 'N/A'})
                  </p>
                ) : (
                  <Link to={`/units/${transferredFrom.id}`} data-cy="unit-renumbered-from-link">
                    {transferredFrom.unitNumber}
                  </Link>
                )}
              </div>
              <div>
                <h6 className="text-dark font-weight-regular">RENUMBERED TO</h6>
                {!transferredTo ? (
                  <p className="text-black">N/A</p>
                ) : [Role.ADMIN_CUSTOMER, Role.CUSTOMER, Role.EXTERNAL].includes(userProfile.role) &&
                  !userProfile.customers.some(({ id }) => id === transferredTo.customer?.id) ? (
                  <p className="text-black" data-cy="unit-renumbered-to">
                    {transferredTo.unitNumber}({transferredTo.customer?.name ?? 'N/A'})
                  </p>
                ) : (
                  <Link to={`/units/${transferredTo.id}`} data-cy="unit-renumbered-to-link">
                    {transferredTo.unitNumber}
                  </Link>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneralInformation;
